import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icons";
import type { ProjectFilm } from "../lib/types";

/* A film, watched properly.
 *
 * A film competing with a heading, a caption, a note, a border and whatever sits
 * below it isn't being watched — it's being glanced at. So the page carries a
 * quiet still, and playing takes the whole screen: black, edge to edge, nothing
 * else on it. The only control is a close, and it fades out of the way too. */

export function FilmFrame({ film, height = "clamp(360px, 62vh, 680px)", theatre = true }: { film: ProjectFilm; height?: string; theatre?: boolean }) {
  const [open, setOpen] = useState(false);
  const [idle, setIdle] = useState(false);
  const [inline, setInline] = useState(!theatre);   // studio preview plays in place

  /* In the theatre the chrome disappears until you reach for it. */
  useEffect(() => {
    if (!open) return;
    let t: number | undefined;
    const wake = () => { setIdle(false); window.clearTimeout(t); t = window.setTimeout(() => setIdle(true), 2200); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    wake();
    window.addEventListener("mousemove", wake);
    window.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("mousemove", wake);
      window.removeEventListener("keydown", esc);
      document.body.style.overflow = "";
    };
  }, [open]);

  const frame = (key: string) => (
    <iframe
      key={key}
      src={film.url}
      title={film.title}
      allow="fullscreen; autoplay"
      referrerPolicy="same-origin"
      style={{ width: "100%", height: "100%", border: "none", display: "block", background: "#FAFAF7" }}
    />
  );

  /* The still on the page: the film's own opening frame, nothing written over
     it but the one thing you need to know — that it plays. */
  const poster = (
    <button onClick={() => (theatre ? setOpen(true) : setInline(true))}
      style={{
        position: "relative", width: "100%", height, border: "none", padding: 0, cursor: "pointer",
        background: "#FAFAF7", borderRadius: 10, overflow: "hidden", display: "block",
      }}>
      <span style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", gap: 14 }}>
        <span style={{ display: "grid", placeItems: "center", width: 62, height: 62, borderRadius: "50%", background: "#1D1D1F", color: "#fff" }}>
          <Icon.present width={21} />
        </span>
        <span style={{ color: "#1D1D1F", fontSize: 15, letterSpacing: 0.2 }}>{film.title}</span>
      </span>
    </button>
  );

  if (!theatre) {
    return (
      <div style={{ width: "100%", height, background: "#FAFAF7", overflow: "hidden", borderRadius: 10 }}>
        {inline ? frame("inline") : poster}
      </div>
    );
  }

  return (
    <>
      {poster}
      {open && createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 220, background: "#000", display: "grid", placeItems: "center" }}>
          {/* the film gets the screen; the frame keeps its own proportions */}
          <div style={{ width: "100%", height: "100%" }}>{frame("theatre")}</div>
          <button onClick={() => setOpen(false)} aria-label="Close"
            style={{
              position: "absolute", top: 16, right: 18, width: 38, height: 38, borderRadius: 19,
              display: "grid", placeItems: "center", cursor: "pointer",
              background: "rgba(0,0,0,.45)", border: "1px solid rgba(255,255,255,.25)", color: "#fff",
              opacity: idle ? 0 : 1, transition: "opacity .45s", pointerEvents: idle ? "none" : "auto",
              backdropFilter: "blur(6px)",
            }}>
            <Icon.close width={16} />
          </button>
        </div>, document.body)}
    </>
  );
}

/** The film on a page: the still, and its name. Nothing else competing with it. */
export function FilmCard({ film, height }: { film: ProjectFilm; height?: string }) {
  return <FilmFrame film={film} height={height} />;
}
